import { Bot, Workflow } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ChartCard } from "@/components/ui/chart-card";
import { ChartEmptyState } from "@/components/ui/chart-empty-state";
import { LoadingState } from "@/components/ui/loading-state";
import { KpiPeriod } from "@/models/org-kpis";
import { formatters } from "@/utils/formatters";
import { useOrgNodes } from "../hooks/useOrgNodes";
import { formatRatio, formatUsd } from "../org-kpis.meta";

interface NodesTabProps {
  organizationId: string;
  period: KpiPeriod;
}

export function NodesTab({ organizationId, period }: NodesTabProps) {
  const { data, isLoading, error, refetch } = useOrgNodes(organizationId, period);

  if (isLoading || error || !data) {
    return (
      <LoadingState
        isLoading={isLoading}
        error={error}
        loadingText="Loading bot nodes..."
        errorTitle="Failed to load bot nodes"
        onRetry={() => refetch()}
      />
    );
  }

  const totalSent = data.nodes.reduce((sum, n) => sum + n.sent, 0);
  const totalNew = data.nodes.reduce((sum, n) => sum + n.new, 0);

  return (
    <div className="space-y-6">
      <ChartCard title="Bots">
        {data.bots.length === 0 ? (
          <p className="text-sm text-muted-foreground">This organization has no bots.</p>
        ) : (
          <ul className="flex flex-wrap gap-3">
            {data.bots.map((bot) => (
              <li
                key={bot.botId}
                className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
              >
                <Bot className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{bot.name}</span>
                {bot.active ? (
                  <Badge variant="success">Live on {bot.channels.join(", ")}</Badge>
                ) : (
                  <Badge variant="outline">Not connected</Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </ChartCard>

      <ChartCard
        title="Most-used nodes"
        description={
          data.trackingSince
            ? `Messages each node sent, and what they cost. Node tracking started on ${formatters.formatDate(data.trackingSince)}; earlier messages aren't attributed to a node.`
            : "Node tracking hasn't recorded anything for this organization yet."
        }
      >
        {data.nodes.length === 0 ? (
          <ChartEmptyState
            icon={Workflow}
            title="No node activity in this period"
            description="Bot messages are counted per node from the day tracking started."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 font-medium">Node</th>
                  <th className="py-2 font-medium">Bot</th>
                  <th className="py-2 text-right font-medium">Sent</th>
                  <th className="py-2 text-right font-medium">Share</th>
                  <th className="py-2 text-right font-medium">Read</th>
                  <th className="py-2 text-right font-medium">Failed</th>
                  <th className="py-2 text-right font-medium">At the time</th>
                  <th className="py-2 text-right font-medium">New pricing</th>
                </tr>
              </thead>
              <tbody>
                {data.nodes.map((node) => {
                  const reached = node.delivered + node.read;
                  return (
                    <tr key={node.nodeId} className="border-b last:border-0 align-top">
                      <td className="py-2">
                        <div className="font-medium">{node.label}</div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {node.nodeType}
                          {!node.exists && (
                            <Badge variant="outline" className="text-[10px] font-normal">
                              deleted since
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-2 text-muted-foreground">{node.botName}</td>
                      <td className="py-2 text-right tabular-nums">
                        {formatters.formatNumber(node.sent)}
                      </td>
                      <td className="py-2 text-right tabular-nums text-muted-foreground">
                        {totalSent
                          ? formatters.formatPercentage((node.sent / totalSent) * 100)
                          : "—"}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatRatio(reached ? node.read / reached : null)}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatters.formatNumber(node.failed)}
                      </td>
                      <td className="py-2 text-right tabular-nums">{formatUsd(node.then)}</td>
                      <td className="py-2 text-right font-medium tabular-nums">
                        {formatUsd(node.new)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-muted-foreground">
              Top {data.nodes.length} nodes: {formatters.formatNumber(totalSent)} messages,{" "}
              {formatUsd(totalNew)} under new pricing. Nodes that send many messages per
              conversation are where flows can usually be shortened.
            </p>
          </div>
        )}
      </ChartCard>
    </div>
  );
}
